/**
 * Vietnamese labels for every enum the API can return.
 *
 * WHY these are exhaustive `Record<Union, string>` and not a lookup with a
 * fallback: if the backend adds a status, TypeScript fails the build here
 * instead of shipping a screen that shows a raw `AWAY_WITH_PERMISSION` to an
 * employee. The fallback helpers below exist only for the handful of fields the
 * API types as a bare `string` (task_type, verification_result, shift status).
 */
import type {
  AppealType,
  AttendanceActionType,
  AttendanceRiskFlag,
  KpiRejectReason,
  LeaveType,
  PresenceStatus,
  RejectReasonCode,
  ScanQrType,
  SwapType,
  TaskStatus,
} from './contracts';

/**
 * Tone drives colour, and it comes from `@sonauto/ui` rather than being
 * redeclared here — a fifth tone added to the design system must not silently
 * mean something different in the Staff App.
 */
export type { Tone } from '@sonauto/ui';
import type { Tone } from '@sonauto/ui';

export const TASK_STATUS_LABEL: Readonly<Record<TaskStatus, string>> = {
  CREATED: 'Mới tạo',
  OFFERED: 'Đang mời nhận',
  ACCEPTED: 'Đã nhận',
  IN_PROGRESS: 'Đang làm',
  WAITING: 'Tạm dừng',
  COMPLETED: 'Hoàn thành',
  REJECTED: 'Đã từ chối',
  REASSIGNED: 'Đã chuyển người khác',
  ESCALATED: 'Đã báo cấp trên',
  CANCELLED: 'Đã huỷ',
  FAILED: 'Thất bại',
  EXPIRED: 'Hết hạn',
};

export const TASK_STATUS_TONE: Readonly<Record<TaskStatus, Tone>> = {
  CREATED: 'neutral',
  OFFERED: 'info',
  ACCEPTED: 'info',
  IN_PROGRESS: 'warning',
  WAITING: 'neutral',
  COMPLETED: 'success',
  REJECTED: 'danger',
  REASSIGNED: 'neutral',
  ESCALATED: 'danger',
  CANCELLED: 'neutral',
  FAILED: 'danger',
  EXPIRED: 'danger',
};

export const PRESENCE_LABEL: Readonly<Record<PresenceStatus, string>> = {
  AVAILABLE: 'Đang rảnh',
  BUSY: 'Đang bận',
  ON_BREAK: 'Đang nghỉ giữa ca',
  SERVING_CUSTOMER: 'Đang phục vụ khách',
  PROCESSING_PAYMENT: 'Đang thanh toán',
  TEMPORARILY_UNAVAILABLE: 'Tạm thời không nhận việc',
  AWAY_WITH_PERMISSION: 'Rời vị trí có phép',
  EMERGENCY: 'Khẩn cấp',
  OFFLINE: 'Ngoài ca',
};

export const PRESENCE_TONE: Readonly<Record<PresenceStatus, Tone>> = {
  AVAILABLE: 'success',
  BUSY: 'warning',
  ON_BREAK: 'info',
  SERVING_CUSTOMER: 'warning',
  PROCESSING_PAYMENT: 'warning',
  TEMPORARILY_UNAVAILABLE: 'neutral',
  AWAY_WITH_PERMISSION: 'info',
  EMERGENCY: 'danger',
  OFFLINE: 'neutral',
};

export const ATTENDANCE_ACTION_LABEL: Readonly<Record<AttendanceActionType, string>> = {
  CHECK_IN: 'Vào ca',
  CHECK_OUT: 'Tan ca',
  BREAK_START: 'Nghỉ giữa ca',
  BREAK_END: 'Quay lại làm việc',
};

export const REJECT_REASON_LABEL: Readonly<Record<RejectReasonCode, string>> = {
  BUSY: 'Đang bận việc khác',
  WRONG_STATION: 'Không đúng khu vực của tôi',
  NO_SKILL: 'Tôi chưa được đào tạo việc này',
  ON_BREAK: 'Đang nghỉ giữa ca',
  TOO_FAR: 'Ở quá xa vị trí cần làm',
  EQUIPMENT_ISSUE: 'Thiếu hoặc hỏng dụng cụ',
  OTHER: 'Lý do khác',
};

/**
 * Why an event earned nothing. Shown verbatim to the employee — an excluded
 * event that is invisible is the fastest way to destroy trust in a KPI system,
 * so every one of these is paired with an appeal button.
 */
export const KPI_REJECT_LABEL: Readonly<Record<KpiRejectReason, string>> = {
  NO_EMPLOYEE: 'Không xác định được nhân viên thực hiện.',
  NOT_ON_SHIFT: 'Thao tác diễn ra ngoài ca làm của bạn.',
  WRONG_BRANCH: 'Thao tác diễn ra ở chi nhánh khác chi nhánh của nhiệm vụ.',
  MISSING_PERMISSION: 'Bạn chưa có quyền thực hiện loại nhiệm vụ này.',
  MISSING_SKILL: 'Bạn chưa có kỹ năng bắt buộc cho nhiệm vụ này.',
  SKILL_LEVEL_TOO_LOW: 'Cấp độ kỹ năng của bạn chưa đạt yêu cầu.',
  OUT_OF_ORDER_TRANSITION: 'Các bước không đúng thứ tự (ví dụ báo hoàn thành khi chưa bắt đầu).',
  DUPLICATE_IDEMPOTENCY_KEY: 'Thao tác trùng với một thao tác đã ghi nhận trước đó.',
  SCANNED_ON_BEHALF: 'Mã QR được quét bằng tài khoản của người khác.',
  IMPLAUSIBLY_FAST: 'Thời gian xử lý ngắn bất thường so với định mức.',
  ORDER_CANCELLED_ABNORMALLY: 'Đơn hàng liên quan đã bị huỷ bất thường.',
  SELF_CREATED_SELF_COMPLETED: 'Bạn vừa tự tạo vừa tự đóng nhiệm vụ mà không có bằng chứng.',
  MISSING_EVIDENCE: 'Nhiệm vụ này bắt buộc có ảnh bằng chứng.',
  POINT_FARMING: 'Mẫu thao tác giống hành vi "cày điểm".',
};

export const RISK_FLAG_LABEL: Readonly<Record<AttendanceRiskFlag, string>> = {
  SKEWED_CLOCK: 'Giờ trên điện thoại lệch nhiều so với giờ máy chủ',
  OUT_OF_GEOFENCE: 'Vị trí nằm ngoài phạm vi cho phép của chi nhánh',
  LOW_GPS_ACCURACY: 'Định vị GPS không đủ chính xác',
  UNREGISTERED_DEVICE: 'Thiết bị chưa được đăng ký',
  DUPLICATE_WINDOW: 'Trùng với một lần chấm công vừa thực hiện',
  SHARED_DEVICE: 'Thiết bị này vừa được người khác dùng để chấm công',
};

/** What the employee should actually DO about each flag. */
export const RISK_FLAG_ADVICE: Readonly<Record<AttendanceRiskFlag, string>> = {
  SKEWED_CLOCK: 'Bật "Tự động đặt ngày giờ" trong cài đặt điện thoại rồi chấm công lại.',
  OUT_OF_GEOFENCE: 'Đến gần cửa hàng hơn rồi thử lại. Nếu bạn đang ở đúng chỗ, hãy gửi khiếu nại chấm công.',
  LOW_GPS_ACCURACY: 'Ra chỗ thoáng hoặc bật Wi-Fi để định vị chính xác hơn, rồi thử lại.',
  UNREGISTERED_DEVICE: 'Nhờ quản lý chi nhánh đăng ký thiết bị này cho bạn.',
  DUPLICATE_WINDOW: 'Bạn vừa chấm công thao tác này. Đợi vài phút trước khi thử lại.',
  SHARED_DEVICE: 'Nếu đây là máy dùng chung của cửa hàng thì bình thường; quản lý sẽ xác nhận.',
};

export const QR_TYPE_LABEL: Readonly<Record<ScanQrType, string>> = {
  TABLE: 'Bàn',
  STATION: 'Trạm / khu vực',
  ORDER: 'Đơn hàng',
  ORDER_ITEM: 'Món trong đơn',
  TICKET: 'Phiếu chế biến',
  ASSET: 'Tài sản / thiết bị',
  LOCATION: 'Vị trí',
};

export const LEAVE_TYPE_LABEL: Readonly<Record<LeaveType, string>> = {
  ANNUAL: 'Nghỉ phép năm',
  SICK: 'Nghỉ ốm',
  UNPAID: 'Nghỉ không lương',
  MATERNITY: 'Nghỉ thai sản',
  EMERGENCY: 'Nghỉ khẩn cấp',
  OTHER: 'Lý do khác',
};

export const SWAP_TYPE_LABEL: Readonly<Record<SwapType, string>> = {
  SWAP: 'Đổi ca với đồng nghiệp',
  GIVEAWAY: 'Nhường ca của tôi',
  PICKUP_OPEN: 'Nhận ca trống',
};

export const APPEAL_TYPE_LABEL: Readonly<Record<AppealType, string>> = {
  KPI_SCORE: 'Điểm KPI',
  TASK_EVENT: 'Một thao tác nhiệm vụ',
  ATTENDANCE: 'Chấm công',
  PAYROLL_DEDUCTION: 'Khoản trừ lương',
};

// ---------------------------------------------------------------------------
// Fields the API types as a bare string — labelled with a safe fallback.
// ---------------------------------------------------------------------------

const URGENCY_LABEL: Readonly<Record<string, string>> = {
  LOW: 'Thấp',
  NORMAL: 'Bình thường',
  HIGH: 'Gấp',
  CRITICAL: 'Rất gấp',
};

export function urgencyLabel(urgency: string): string {
  return URGENCY_LABEL[urgency] ?? urgency;
}

export function urgencyTone(urgency: string): Tone {
  if (urgency === 'CRITICAL') return 'danger';
  if (urgency === 'HIGH') return 'warning';
  return 'neutral';
}

const APPROVAL_STATUS_LABEL: Readonly<Record<string, string>> = {
  SUBMITTED: 'Đã gửi',
  PENDING: 'Chờ duyệt',
  UNDER_REVIEW: 'Đang xem xét',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Bị từ chối',
  CANCELLED: 'Đã huỷ',
  DISBURSED: 'Đã chi',
  PARTIALLY_APPROVED: 'Duyệt một phần',
};

export function approvalStatusLabel(status: string): string {
  return APPROVAL_STATUS_LABEL[status] ?? status;
}

export function approvalStatusTone(status: string): Tone {
  if (status === 'APPROVED' || status === 'DISBURSED') return 'success';
  if (status === 'REJECTED') return 'danger';
  if (status === 'CANCELLED') return 'neutral';
  return 'info';
}

const VERIFICATION_LABEL: Readonly<Record<string, string>> = {
  PASSED: 'Hợp lệ',
  PENDING_REVIEW: 'Chờ quản lý xác minh',
  FAILED: 'Không hợp lệ',
  MANUAL_OVERRIDE: 'Quản lý xác nhận thủ công',
};

export function verificationLabel(result: string): string {
  return VERIFICATION_LABEL[result] ?? result;
}

export function verificationTone(result: string): Tone {
  if (result === 'PASSED' || result === 'MANUAL_OVERRIDE') return 'success';
  if (result === 'PENDING_REVIEW') return 'warning';
  return 'danger';
}

const ATTENDANCE_DAY_STATUS_LABEL: Readonly<Record<string, string>> = {
  OK: 'Đủ công',
  INCOMPLETE: 'Chưa tan ca',
  LATE: 'Đi muộn',
  ABSENT: 'Vắng',
  EARLY_LEAVE: 'Về sớm',
  OVERTIME: 'Có tăng ca',
};

export function dayStatusLabel(status: string): string {
  return ATTENDANCE_DAY_STATUS_LABEL[status] ?? status;
}

/**
 * `task_type` is an open catalogue in the database, so this is a best-effort
 * prettifier rather than an exhaustive map: an unknown type shows as-is instead
 * of being hidden.
 */
const TASK_TYPE_LABEL: Readonly<Record<string, string>> = {
  SERVE_FOOD: 'Mang món ra bàn',
  SERVE_DRINK: 'Mang nước ra bàn',
  CLEAR_TABLE: 'Dọn bàn',
  SETUP_TABLE: 'Setup bàn',
  TAKE_ORDER: 'Nhận gọi món',
  DELIVER_BILL: 'Mang hoá đơn',
  PREPARE_ITEM: 'Chế biến món',
  RESTOCK: 'Bổ sung hàng',
  CLEAN: 'Vệ sinh',
  CUSTOMER_REQUEST: 'Yêu cầu của khách',
  VERIFY_ORDER: 'Xác minh đơn khách đặt',
  INVENTORY_COUNT: 'Kiểm kê kho',
};

export function taskTypeLabel(taskType: string): string {
  return TASK_TYPE_LABEL[taskType] ?? taskType;
}
